//
// In Nov 2023 this was copied from DocumentUploadBoxWithContext in Hiko so it could be used in the DLSP project.
// References to hikoContext were changed to mahiContext.
// A call to fetchMultipartFormDataWithAuth was changed to putMultipartFormDataWithAuth.
//
// Component was then modified to accept app context (mahiContext or hikoContext) and API target
// Reccomend: create a library component from this DocumentUploadWithContext that takes context as a prop.
//
// This component is also really similar to MickUploadBox.
// Main difference is this component deletes thumbnail of uploaded image when upload is complete.
// This component does not implement reove function, whereas MickUploadBox does.
//
import { Button, Stack } from '@mui/material';
import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react'
import { PhotoCamera } from '@material-ui/icons';
import { styled } from '@mui/system';
import { nanoid } from 'nanoid';
import { ClientImagePreview, hikoException } from '@northpowerdev/np-react-utils';
import { resizeFile } from '../../utils/ResizeImageFile';
import { putMultipartFormDataWithAuth } from '../../data/hikoFetch';
import ImagePasteBox from './ImagePasteBox';

// var mammoth = require("mammoth/mammoth.browser.js"); // DEBUG

const env = process.env.REACT_APP_ENV
const envConfig = require(`../../config/config.${env}.json`)

const StyledInput = styled('input')({
  display: 'none',
});

/**
 * 
 * Uploads a file from the user's device to a 'Jira-style' Hiko BFF API such as the 1c Plans API.
 * 
 * @param {object} props 
 * @param {String} props.uploadPath bffHiko relative path to endpoint that accepts POST multipart-form-data and returns e.g. {"fileKey":"10040"}
 * @param {File[]} props.initialFiles list of File Objects (see files state variable below) which have already been uploaded. Only used when component in constructed, never on re-render.
 * @param {String[]} props.fileTypes list of MIME types accepted for upload
 * @param {Function} props.statusChange parent supplied function which is called at various times during image upload. 
 * @param {object} props.appContext hikoContext or mahiContext
 * @param {string} props.bffApi "bffHiko" or "bffMahi"
 * @returns {JSX.Element} JSX.Element
 */
export default function DocumentUploadBoxWithContext({
        uploadPath,
        initialFiles = [],
        fileTypes = ['image/png', 'image/jpeg', 'application/pdf'],
        statusChange,
        appContext,
        bffApi,
    }) {

    const maxConcurrentUploads = 2

    const [files, setFiles] = useState([])
  
    const emitStatusChangeCallback = useCallback(
        (allFiles, changeFileSpecs) => {

            const emitStatusChange = (allFiles, changeFileSpecs) => {
                console.log('emitStatusChange allFiles', allFiles)
                // debugFiles(allFiles) 
                
                const isUploaderBusy = allFiles.filter((f) => ['Uploading'].includes(f.status)).length > 0
                statusChange(isUploaderBusy, changeFileSpecs)
            }

            emitStatusChange(allFiles, changeFileSpecs)
        },
        [statusChange],
    )

    useEffect(() => {
        //
        // Called once when we construct the uploader.
        // Gives us the chance to set up any files that were uploaded earlier.
        //
        const initialUploadedFiles = initialFiles?.filter((f) => f.status === 'Uploaded')
        setFiles(initialUploadedFiles)
    // eslint-disable-next-line
    }, [])

    useEffect(() => {
        //
        // This is the core of the uploader.
        // It is executed whenever files are added, removed
        // or have their status changed (e.g. upload complete).
        //
        const abortController = new AbortController()
        
        const uploadAsync = async (someFileObject) => {
            console.log('uploadAsync', someFileObject)
            someFileObject.status = 'Uploading'

            const options = {
                // method: 'POST'
            }

            let resizeError = false
            if (someFileObject?.file?.type?.includes("image")) {
                try {
                    //
                    // This is an image so try to resize it.
                    //
                    const resizedFile = await resizeFile(
                        someFileObject.file,
                        envConfig.resizer.maxWidth,
                        envConfig.resizer.maxHeight,
                        'jpeg',
                        envConfig.resizer.quality,
                    )
                    someFileObject.file = resizedFile
                } catch (e) {
                    hikoException('Problem resizing your image file', e)
                    resizeError = true
                }
            }

            let uploadSucceeded = false
            if (!resizeError) {
                try {
                    // const response = await fetchMultipartFormDataWithAuth(mahiContext, 'bffMahi', uploadPath, someFileObject.file, options, abortController.signal)
                    const response = await putMultipartFormDataWithAuth(appContext, bffApi, uploadPath, someFileObject.file, options, abortController.signal)
                    if (response.fileKey) {
                        someFileObject.fileKey = response.fileKey
                        someFileObject.key = response.fileKey
                        someFileObject.url = '' // url unimplemented
                        uploadSucceeded = true
                    }
                } catch (e) {
                    hikoException('Problem uploading image data to server', e)
                }
            }

            let fileChangeSpecs = []
            if (uploadSucceeded) {
                someFileObject.status = 'Uploaded'
                fileChangeSpecs.push({action: 'Add', file: someFileObject})
            } else {
                someFileObject.status = 'Error'
            }

            //
            // Delete image from files array after successful upload.
            // This is a bit aggressive. Better to wait until all uploads are done?
            //
            if (someFileObject.status === 'Uploaded') {
                setFiles(existingFiles => existingFiles.filter(ef => ef.fileKey !== someFileObject.fileKey))
            }
            emitStatusChangeCallback(files, fileChangeSpecs)

        }

        //
        // If there are no files we have nothing to upload.
        //
        if (!files) {
            return
        }

        //
        // See how many uploads are in progress.
        //
        const filesUploading = files.filter((f) => f.status === 'Uploading').length
        if (filesUploading < maxConcurrentUploads) {
            //
            // We have capacity to start a new upload.
            // Check to see if there are unprocessed files in the files list.
            //
            const filesQueued = files.filter((f) => f.status === 'Queued')
            const newUploadsAvailable = maxConcurrentUploads - filesUploading
            let newUploadsStarted = false
            for (let i = 0 ; i < filesQueued.length; i++) {
                if (i >= newUploadsAvailable) {
                    break
                }
                newUploadsStarted = true
                uploadAsync(filesQueued[i]) // run in background
            }
            if (newUploadsStarted) {
                //
                // Tell parent component that the uploader is busy.
                // The file change specs list is empty because we haven't completed any uploads.
                //
                emitStatusChangeCallback(files, [])
                //
                // Set the files state variable again to ensure sub-components update with changed status.
                //
                // setFiles([...files]) 
            }
        }

    }, [files, uploadPath, appContext, bffApi, emitStatusChangeCallback])

    const handleInputFileChange = (e) => {
        // console.log('handleInputFileChange e.target.files', e.target.files)
        const fileList = e.target.files
        const fileArray = [...files]
        for (let i = 0; i < fileList.length; i++) {
            const newFile = fileList.item(i)
            const newFileObject = {
                file: newFile,
                fileKey: nanoid(10),
                fileName: newFile.name,
                url: null,
                status: 'Queued'
            }
            fileArray.push(newFileObject)
        }
        setFiles(fileArray)
    }

    const handleFilePasted = (blob) => {
        console.log('DocumentUploadBoxWithContext handle file pasted')
        const newFileObject = {
            file: blob,
            fileKey: nanoid(10),
            fileName: 'Anonymous Blob From DocumentUploadBoxWithContext',
            url: null,
            status: 'Queued'
        }
        console.log('DocumentUploadBoxWithContext setFiles')
        setFiles(files => [...files, newFileObject])

    }
    const handleRemove = (fileKey) => {
        console.log('handleRemove unimplemented for fileKey', fileKey)
    }

    return (
        <Box>
            <ImagePasteBox onPaste={handleFilePasted} sx={{border: '2px solid #eaeaea', minHeight: '200px'}}>
                <Box sx={{p: 2}}>
                    <label htmlFor="file-upload-input">
                        
                        <Button variant="outlined" component="label" endIcon={<PhotoCamera />}> 
                            Choose Files
                            <StyledInput
                                //accept="image/png, image/jpeg, appsication/pdf"
                                accept={fileTypes}
                                key="file-upload-input"
                                onChange={handleInputFileChange}
                                multiple
                                type="file"
                            />
                        </Button>
                    </label>
                </Box>
                <Box>
                    <Stack direction="row" sx={{flexWrap: 'wrap', my: 2}}>
                        {files && files.map((f) => {
                            return (
                                <ClientImagePreview
                                    key={f.fileKey}
                                    id={f.fileKey}
                                    file={f.file}
                                    status={f.status}
                                    size={180}
                                    remove={handleRemove}
                                    sx={{mb: 2, mr: 2}}
                                />
                            )
                        })
                        }
                    </Stack>
                </Box>
            </ImagePasteBox>


            {/* <DebugBox title="files">
                <List>
                    {files && files.map((f) => {
                        return (
                            <ListItem key={f.fileKey}>
                                url:     {f.url}<br />
                                status:  {f.status}<br />
                                name: {f.file.name}<br />
                                size: {f.file.size}<br />
                                type: {f.file.type}<br />
                            </ListItem>
                        )
                    })}
                </List>
            </DebugBox> */}
        </Box>
    )
}

